import { useState } from 'react'

import { useAuth } from './auth/AuthProvider'
import { InitialPasswordChange } from './auth/InitialPasswordChange'
import { LoginPage } from './auth/LoginPage'
import { canManageUsers, canWriteOperationalData } from './auth/permissions'
import { UserManagement } from './admin/UserManagement'
import { Dashboard } from './dashboard/Dashboard'
import { ImportPanel } from './import/ImportPanel'

type WorkspaceSection = 'dashboard' | 'imports' | 'users'

export function App() {
  const { error, loading, profile, session, signOut } = useAuth()
  const [dataRevision, setDataRevision] = useState(0)
  const [section, setSection] = useState<WorkspaceSection>('dashboard')
  const canImport = Boolean(profile && canWriteOperationalData(profile))
  const canManage = Boolean(profile && canManageUsers(profile))

  if (loading) {
    return (
      <main className="loading-screen" aria-live="polite">
        <span className="loading-mark">hvm</span>
        <p>Validando acesso…</p>
      </main>
    )
  }

  if (!session || !profile) {
    return <LoginPage sessionError={error} />
  }

  if (profile.must_change_password) {
    return (
      <InitialPasswordChange
        accessToken={session.access_token}
        fullName={profile.full_name}
        onSignOut={signOut}
      />
    )
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <button
          type="button"
          className="brand brand-button"
          aria-label="HVM - dashboard"
          onClick={() => setSection('dashboard')}
        >
          hvm
        </button>

        <nav className="workspace-nav" aria-label="Navegação principal">
          <button
            type="button"
            className={section === 'dashboard' ? 'workspace-nav__item workspace-nav__item--active' : 'workspace-nav__item'}
            aria-current={section === 'dashboard' ? 'page' : undefined}
            onClick={() => setSection('dashboard')}
          >
            Dashboard
          </button>

          {canImport && (
            <button
              type="button"
              className={section === 'imports' ? 'workspace-nav__item workspace-nav__item--active' : 'workspace-nav__item'}
              aria-current={section === 'imports' ? 'page' : undefined}
              onClick={() => setSection('imports')}
            >
              Importações
            </button>
          )}

          {canManage && (
            <button
              type="button"
              className={section === 'users' ? 'workspace-nav__item workspace-nav__item--active' : 'workspace-nav__item'}
              aria-current={section === 'users' ? 'page' : undefined}
              onClick={() => setSection('users')}
            >
              Usuários
            </button>
          )}
        </nav>

        <div className="account-summary">
          <div>
            <strong>{profile.full_name}</strong>
            <span>{profile.role === 'ADMIN' ? 'Administrador' : 'Gestão'}</span>
          </div>
          <button className="secondary-button" onClick={() => void signOut()}>
            Sair
          </button>
        </div>
      </header>

      <div className="workspace-context">
        <div>
          <span className="workspace-context__label">Controle de dados</span>
          <strong>
            {section === 'dashboard'
              ? 'Visão de indicadores'
              : section === 'imports'
                ? 'Gestão de importações'
                : 'Usuários e acessos'}
          </strong>
        </div>
        <span className="workspace-context__role">
          {profile.role === 'ADMIN' ? 'Acesso administrativo' : 'Consulta de gestão'}
        </span>
      </div>

      {section === 'dashboard' && (
        <Dashboard key={`${profile.id}-${dataRevision}`} />
      )}

      {section === 'imports' && canImport && (
        <ImportPanel
          onPublished={() => {
            setDataRevision(value => value + 1)
            setSection('dashboard')
          }}
        />
      )}

      {section === 'users' && canManage && (
        <UserManagement
          accessToken={session.access_token}
          currentUserId={profile.id}
        />
      )}
    </main>
  )
}
